"use client";

import React, { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
  format,
  isSameDay,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isBefore,
  startOfToday,
  addMonths,
  differenceInDays,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type BlockedDate } from "@/src/shared/types/blockedDate";
import { cn } from "@/src/lib/utils";

type DatePickerProps = {
  selectedDates: { checkIn: Date | null; checkOut: Date | null };
  onDateChange: (dates: {
    checkIn: Date | null;
    checkOut: Date | null;
  }) => void;
  onConfirm?: () => void;
  setActiveDateField?: (field: "checkIn" | "checkOut") => void;
  isDetailsPage?: boolean;
  title?: string;
  unableGlobalScroll?: boolean;
  isMonthSingleView?: boolean;
  containerClass?: string;
  selectionVersion?: "v1" | "v2"; // v2 is default
  blockedDates?: BlockedDate[];
  loading?: boolean;
  error?: string | null;
  checkoutOnlyDates?: string[]; // dates allowed only as checkout (cannot start a stay)
  /**
   * "app": the app's select date screen (`SelectDatePage`) — months scrolling down, pink
   * check-in/out circles over a light red range, Fri/Sat in red, booked days in red
   * circles; no title or buttons (the page around it has them).
   */
  appearance?: "default" | "app" | "popover";
};

/** The app calendar's months: loaded a year at a time, up to the end of this year. */
const APP_MONTH_STEP = 12;
const APP_LAST_YEAR = 2100;

const DatePicker: React.FC<DatePickerProps> = ({
  selectedDates = { checkIn: new Date(), checkOut: null },
  onDateChange,
  onConfirm,
  setActiveDateField,
  isDetailsPage = false,
  title = "",
  unableGlobalScroll = false,
  isMonthSingleView = false,
  containerClass,
  selectionVersion = "v2",
  blockedDates = [],
  loading: _loading = false,
  error: _error = null,
  checkoutOnlyDates = [],
  appearance = "default",
}) => {
  const isPopover = appearance === "popover";
  const today = startOfToday();
  const [currentMonth, setCurrentMonth] = useState(
    selectedDates.checkIn || today,
  );
  const [visibleMonthsCount, setVisibleMonthsCount] = useState(2);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [alertMsg, setAlertMsg] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const nextMonth = addMonths(currentMonth, 1);

  const blockedDateSet = React.useMemo(() => {
    return new Set(blockedDates.map((d) => d.date));
  }, [blockedDates]);

  const bookedDateSet = React.useMemo(() => {
    return new Set(
      blockedDates
        .filter((d) => d.count > 0 || d.id !== undefined)
        .map((d) => d.date),
    );
  }, [blockedDates]);

  const checkoutOnlyDatesSet = React.useMemo(() => {
    return new Set(checkoutOnlyDates);
  }, [checkoutOnlyDates]);

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    if (isDetailsPage || unableGlobalScroll) return;

    const isMobile = window?.innerWidth < 768;
    if (isMobile) return;

    const stopPropagation = (e: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const delta = e.deltaY;

      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight;

      // Prevent body scroll when modal content is scrollable
      if ((isAtTop && delta < 0) || (isAtBottom && delta > 0)) {
        e.preventDefault();
      }
    };

    el.addEventListener("wheel", stopPropagation, { passive: false });

    return () => {
      el.removeEventListener("wheel", stopPropagation);
    };
  }, [isDetailsPage, unableGlobalScroll]);

  // Helper to check if any date in a range is blocked
  // The app's calendar scrolls on to 2100 (Syncfusion's default maxDate): months are added
  // a year at a time as the end of the list comes near.
  const appMonthsMax =
    (APP_LAST_YEAR - today.getFullYear()) * 12 + (12 - today.getMonth());
  const [appMonthCount, setAppMonthCount] = useState(APP_MONTH_STEP);
  const appEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (appearance !== "app") return;
    const end = appEndRef.current;
    if (!end) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting)
          setAppMonthCount((n) => Math.min(n + APP_MONTH_STEP, appMonthsMax));
      },
      { rootMargin: "600px 0px" },
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [appearance, appMonthCount, appMonthsMax]);

  const isRangeBlocked = (start: Date, end: Date) => {
    let day = start;
    while (day <= end) {
      const dayStr = format(day, "yyyy-MM-dd");
      if (blockedDateSet.has(dayStr)) return true;
      day = addDays(day, 1);
    }
    return false;
  };

  const handleDayClick = (day: Date) => {
    const dayStr = format(day, "yyyy-MM-dd");
    const isCheckoutOnly = checkoutOnlyDatesSet.has(dayStr);
    if (bookedDateSet.has(dayStr)) {
      enqueueSnackbar("This date is already booked by someone", {
        variant: "warning",
      });
      return;
    }
    if (isBefore(day, today)) return;
    if (blockedDateSet.has(dayStr)) return; // fully blocked

    const { checkIn, checkOut } = selectedDates;

    // Cannot select a checkout-only date as initial check-in
    if (!checkIn && isCheckoutOnly) {
      enqueueSnackbar("This date can only be used as a checkout day", {
        variant: "info",
      });
      return;
    }

    // v2 logic: always reset if both are set
    if (selectionVersion === "v2") {
      if (!checkIn) {
        // disallow checkout-only as check-in
        if (isCheckoutOnly) {
          enqueueSnackbar("Choose a different date for check-in", {
            variant: "info",
          });
          return;
        }
        onDateChange({ checkIn: day, checkOut: null });
        if (setActiveDateField) setActiveDateField("checkOut");
        setAlertMsg(null);
        return;
      }
      if (checkIn && !checkOut) {
        if (isBefore(day, checkIn)) {
          // moving check-in earlier (ensure not checkout-only)
          if (isCheckoutOnly) {
            enqueueSnackbar("This date can't be used as check-in", {
              variant: "info",
            });
            return;
          }
          onDateChange({ checkIn: day, checkOut: null });
          setAlertMsg(null);
        } else if (!isSameDay(day, checkIn)) {
          // selecting checkout (allow checkout-only)
          if (!isRangeBlocked(checkIn, day)) {
            onDateChange({ checkIn, checkOut: day });
            setAlertMsg(null);
          } else {
            setAlertMsg(
              "You cannot select a range that includes blocked dates.",
            );
            enqueueSnackbar(
              "You cannot select a range that includes blocked dates.",
              { variant: "error" },
            );
          }
        }
        return;
      }
      // If both are set, always start new selection
      if (checkIn && checkOut) {
        if (isCheckoutOnly) {
          enqueueSnackbar("This date can't be used as check-in", {
            variant: "info",
          });
          return;
        }
        onDateChange({ checkIn: day, checkOut: null });
        if (setActiveDateField) setActiveDateField("checkOut");
        setAlertMsg(null);
        return;
      }
      return;
    }

    // Original (non v2) logic
    if (!checkIn) {
      if (isCheckoutOnly) {
        enqueueSnackbar("This date can only be used as a checkout day", {
          variant: "info",
        });
        return;
      }
      onDateChange({ checkIn: day, checkOut: null });
      if (setActiveDateField) {
        setActiveDateField("checkOut");
      }
      setAlertMsg(null);
      return;
    }

    if (checkIn && checkOut) {
      if (isBefore(day, checkIn)) {
        if (isCheckoutOnly) {
          enqueueSnackbar("This date can't be used as check-in", {
            variant: "info",
          });
          return;
        }
        onDateChange({ checkIn: day, checkOut: null });
        setAlertMsg(null);
      } else if (!isSameDay(day, checkIn)) {
        // Prevent selecting range with blocked dates
        if (!isRangeBlocked(checkIn, day)) {
          onDateChange({ checkIn, checkOut: day });
          setAlertMsg(null);
        } else {
          setAlertMsg("You cannot select a range that includes blocked dates.");
        }
      }
      return;
    }

    if (checkIn && !checkOut) {
      if (isBefore(day, checkIn)) {
        if (isCheckoutOnly) {
          enqueueSnackbar("This date can't be used as check-in", {
            variant: "info",
          });
          return;
        }
        onDateChange({ checkIn: day, checkOut: null });
        setAlertMsg(null);
      } else if (!isSameDay(day, checkIn)) {
        // Prevent selecting range with blocked dates
        if (!isRangeBlocked(checkIn, day)) {
          onDateChange({ checkIn, checkOut: day });
          setAlertMsg(null);
        } else {
          setAlertMsg("You cannot select a range that includes blocked dates.");
        }
      }
    }
  };

  const generateMonthGrid = (month: Date) => {
    const startDate = startOfWeek(startOfMonth(month));
    const endDate = endOfWeek(endOfMonth(month));
    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  const loadMoreMonths = () => {
    setVisibleMonthsCount((prev) => prev + 2);
  };

  const dynamicTitle = (() => {
    if (selectedDates.checkIn && selectedDates.checkOut) {
      const totalNights = differenceInDays(
        selectedDates.checkOut,
        selectedDates.checkIn,
      );
      return `Selected: ${format(selectedDates.checkIn, "d MMM")} - ${format(selectedDates.checkOut, "d MMM")} (${totalNights} night${totalNights !== 1 ? "s" : ""})`;
    }
    if (selectedDates.checkIn) {
      return `${format(selectedDates.checkIn, "d MMM")} - Select checkout date`;
    }
    return title || "Select date range";
  })();

  const generateMonthsArray = () => {
    const months = [];
    for (let i = 0; i < visibleMonthsCount; i++) {
      months.push(addMonths(currentMonth, i));
    }
    return months;
  };

  const renderMobileMonth = (month: Date, _index: number) => {
    return (
      <div key={month.toString()} className="mb-8 w-full">
        {/* Month ChatHeading */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => setCurrentMonth((m) => addMonths(m, -1))}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Previous month"
          >
            <ChevronLeft size={20} />
          </button>

          <h3 className="text-lg font-semibold text-center flex-1">
            {format(month, "MMMM yyyy")}
          </h3>

          <button
            type="button"
            onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
            className="p-2 rounded-full hover:bg-gray-100"
            aria-label="Next month"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Days of week header */}
        <div className="grid grid-cols-7 mb-4 text-xs font-medium text-ink-gray">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <div key={d} className="h-8 flex items-center justify-center">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar Grid: rows apart, columns touching, so the range runs unbroken. */}
        <div className="grid grid-cols-7 gap-y-2">
          {generateMonthGrid(month).map((day) => {
            const { checkIn, checkOut } = selectedDates;
            const isDayDisabled = isBefore(day, today);
            const isCurrentMonth = day.getMonth() === month.getMonth();
            // Blocked date check
            const dayStr = format(day, "yyyy-MM-dd");
            const isBooked = bookedDateSet.has(dayStr);
            const isDisabled = isDayDisabled || !isCurrentMonth;
            const isCheckoutOnly = checkoutOnlyDatesSet.has(dayStr);

            const isCheckIn = checkIn && isSameDay(day, checkIn);
            const isCheckOut = checkOut && isSameDay(day, checkOut);
            const isWithinRange =
              checkIn &&
              checkOut &&
              isWithinInterval(day, {
                start: startOfDay(checkIn),
                end: startOfDay(checkOut),
              });

            // The cell is as tall as the day's circle, so the range band matches it.
            let wrapperClasses = "flex items-center justify-center h-8";
            let btnClasses =
              "h-8 w-8 flex items-center justify-center text-sm font-medium transition-colors";

            if (isBooked) {
              btnClasses +=
                " bg-brand-100 text-brand-700 font-bold border border-brand-300 rounded-full";
            } else if (isDisabled) {
              btnClasses += " text-ink-faint cursor-not-allowed rounded-full";
            } else {
              btnClasses += " cursor-pointer";
              const isEndpoint = isCheckIn || isCheckOut;

              // The range runs circle to circle: the check-in cell fills only its right
              // half and the check-out cell its left half, so no colour shows past the ends.
              if (isWithinRange) {
                if (isCheckIn && !isCheckOut)
                  wrapperClasses +=
                    " bg-[linear-gradient(to_right,transparent_50%,#FCE7EE_50%)]";
                else if (isCheckOut && !isCheckIn)
                  wrapperClasses +=
                    " bg-[linear-gradient(to_left,transparent_50%,#FCE7EE_50%)]";
                else if (!isEndpoint) wrapperClasses += " bg-[#FCE7EE]";
              }

              if (isEndpoint) {
                btnClasses += " bg-[#C2185B] text-white rounded-full";
              } else if (isCheckoutOnly) {
                btnClasses +=
                  " bg-brand-100 text-brand-700 font-bold border border-brand-300 rounded-full";
              } else {
                btnClasses += " hover:bg-[#F8BDD1] rounded-full";
              }
            }

            return (
              <div
                key={format(day, "yyyy-MM-dd")}
                className={wrapperClasses}
                title={
                  isBooked
                    ? "Booked"
                    : isCheckoutOnly
                      ? "Checkout only"
                      : undefined
                }
              >
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleDayClick(day)}
                  className={btnClasses}
                >
                  {format(day, "d")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDesktopMonth = (month: Date) => {
    return (
      <div key={month.toString()}>
        <div className="grid grid-cols-7 mb-4 text-xs font-medium text-ink-gray">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
            <div key={d} className="h-10 flex items-center justify-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {generateMonthGrid(month).map((day) => {
            const { checkIn, checkOut } = selectedDates;
            const isDayDisabled = isBefore(day, today);
            const isCurrentMonth = day.getMonth() === month.getMonth();
            // Blocked date check
            const dayStr = format(day, "yyyy-MM-dd");
            const isBooked = bookedDateSet.has(dayStr);
            const isDisabled = isDayDisabled || !isCurrentMonth;
            const isCheckoutOnly = checkoutOnlyDatesSet.has(dayStr);

            const isCheckIn = checkIn && isSameDay(day, checkIn);
            const isCheckOut = checkOut && isSameDay(day, checkOut);
            const isWithinRange =
              checkIn &&
              checkOut &&
              isWithinInterval(day, {
                start: startOfDay(checkIn),
                end: startOfDay(checkOut),
              });

            let wrapperClasses = "flex items-center justify-center h-10";
            let btnClasses =
              "h-10 w-10 flex items-center justify-center text-sm font-medium transition-colors";

            if (isBooked) {
              btnClasses +=
                " bg-brand-100 text-brand-700 font-bold border border-brand-300 rounded-full";
            } else if (isDisabled) {
              btnClasses += " text-ink-faint cursor-not-allowed rounded-full";
            } else {
              btnClasses += " cursor-pointer";
              const isEndpoint = isCheckIn || isCheckOut;

              // The range runs circle to circle: the check-in cell fills only its right
              // half and the check-out cell its left half, so no colour shows past the ends.
              if (isWithinRange) {
                if (isCheckIn && !isCheckOut)
                  wrapperClasses +=
                    " bg-[linear-gradient(to_right,transparent_50%,#FCE7EE_50%)]";
                else if (isCheckOut && !isCheckIn)
                  wrapperClasses +=
                    " bg-[linear-gradient(to_left,transparent_50%,#FCE7EE_50%)]";
                else if (!isEndpoint) wrapperClasses += " bg-[#FCE7EE]";
              }

              if (isEndpoint) {
                btnClasses += isPopover
                  ? " bg-brand-dark text-white rounded-full"
                  : " bg-[#C2185B] text-white rounded-full";
              } else if (isCheckoutOnly) {
                btnClasses +=
                  " bg-brand-100 text-brand-700 font-bold border border-brand-300 rounded-full";
              } else {
                btnClasses += " hover:bg-[#F8BDD1] rounded-full";
              }
            }

            return (
              <div
                key={format(day, "yyyy-MM-dd")}
                className={wrapperClasses}
                title={
                  isBooked
                    ? "Booked"
                    : isCheckoutOnly
                      ? "Checkout only"
                      : undefined
                }
              >
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleDayClick(day)}
                  className={btnClasses}
                >
                  {format(day, "d")}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // The app's calendar: one month after another, scrolling down from this month.
  const renderAppMonth = (month: Date) => (
    <div key={month.toString()} className="pb-6">
      {/* Syncfusion's month header: 16px regular, left aligned, held at the top while its
          month scrolls (the next month's header pushes it off). */}
      <h3 className="sticky top-0 z-10 flex h-10 items-center bg-white px-2 text-16 text-ink-black/[.87]">
        {format(month, "MMMM yyyy")}
      </h3>
      <div className="grid grid-cols-7 pb-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div
            key={d}
            className="flex h-8 items-center justify-center text-14 font-semibold text-ink-gray"
          >
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {generateMonthGrid(month).map((day) => {
          const dayStr = format(day, "yyyy-MM-dd");
          if (day.getMonth() !== month.getMonth())
            return <div key={dayStr} className="h-11" />;
          const { checkIn, checkOut } = selectedDates;
          const isPast = isBefore(day, today);
          const isBooked = bookedDateSet.has(dayStr);
          const isCheckIn = !!checkIn && isSameDay(day, checkIn);
          const isCheckOut = !!checkOut && isSameDay(day, checkOut);
          const isEndpoint = isCheckIn || isCheckOut;
          const inRange =
            !!checkIn &&
            !!checkOut &&
            isWithinInterval(day, {
              start: startOfDay(checkIn),
              end: startOfDay(checkOut),
            });
          // Friday and Saturday, the app's weekend days.
          const isWeekend = day.getDay() === 5 || day.getDay() === 6;
          return (
            <div
              key={dayStr}
              className={cn(
                "flex h-11 items-center justify-center",
                // The range runs from circle to circle: the check-in cell fills only its right
                // half, the check-out cell its left half, so no colour shows past the ends.
                inRange && !isEndpoint && "bg-[#FF000015]",
                inRange &&
                  isCheckIn &&
                  !isCheckOut &&
                  "bg-[linear-gradient(to_right,transparent_50%,#FF000015_50%)]",
                inRange &&
                  isCheckOut &&
                  !isCheckIn &&
                  "bg-[linear-gradient(to_left,transparent_50%,#FF000015_50%)]",
              )}
            >
              <button
                type="button"
                disabled={isPast}
                onClick={() => handleDayClick(day)}
                title={
                  isBooked
                    ? "Booked"
                    : checkoutOnlyDatesSet.has(dayStr)
                      ? "Checkout only"
                      : undefined
                }
                className={cn(
                  // Syncfusion's month cells: 13px at 87% black, past days at 38%.
                  "flex h-10 w-10 items-center justify-center rounded-full text-13",
                  isEndpoint
                    ? "bg-brand text-white"
                    : isPast
                      ? "cursor-not-allowed text-ink-black/[.38]"
                      : isBooked
                        ? "bg-danger-soft text-white"
                        : isWeekend
                          ? "font-semibold text-danger-soft"
                          : isSameDay(day, today)
                            ? "text-brand"
                            : "text-ink-black/[.87]",
                  !isEndpoint && isSameDay(day, today) && "ring-1 ring-brand",
                )}
              >
                {format(day, "d")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (appearance === "app") {
    return (
      <div className={cn("w-full", containerClass)}>
        {alertMsg && (
          <div className="sticky top-0 z-10 mb-2 w-full rounded bg-red-50 py-2 text-center text-sm text-danger">
            {alertMsg}
          </div>
        )}
        {Array.from({ length: appMonthCount }, (_, i) =>
          renderAppMonth(addMonths(startOfMonth(today), i)),
        )}
        <div ref={appEndRef} aria-hidden className="h-px" />
      </div>
    );
  }

  // Dynamic container classes based on isDetailsPage
  const containerClasses = isPopover
    ? `flex w-full flex-col rounded-3xl border border-[#EBEBEB] bg-white shadow-[0_6px_20px_rgba(0,0,0,0.2)] ${containerClass}`
    : `bg-white rounded-3xl  w-full mx-auto ${
        isMonthSingleView ? "md:w-full" : "md:max-w-[850px]"
      } flex flex-col h-full max-h-[80vh] md:max-h-none ${
        isDetailsPage ? "shadow-sm" : "shadow-sm md:shadow-xl"
      } ${containerClass}`;

  return (
    <div ref={scrollRef} className={containerClasses}>
      {/* Title and Date Summary for Details Page - Top Position */}
      {isDetailsPage && (
        <div className="pb-0">
          <h2 className="text-base font-semibold mb-2">{dynamicTitle}</h2>
          {/* DateRangeSummary removed to avoid double title */}
        </div>
      )}

      {/* Desktop ChatHeading */}
      <div
        className={`hidden md:flex items-center justify-between flex-shrink-0 ${isPopover ? "mb-1 px-4 pt-5" : "mb-8 p-4 pb-0"}`}
      >
        <button
          type="button"
          onClick={() => setCurrentMonth((m) => addMonths(m, -1))}
          className="p-2 rounded-full hover:bg-gray-100"
          aria-label="Previous month"
        >
          <ChevronLeft size={20} />
        </button>

        {isMonthSingleView ? (
          <h3 className="text-lg font-semibold flex-1 text-center">
            {format(currentMonth, "MMMM yyyy")}
          </h3>
        ) : (
          <div className="flex space-x-16">
            <h3 className="text-lg font-semibold w-48 text-center">
              {format(currentMonth, "MMMM yyyy")}
            </h3>
            <h3 className="text-lg font-semibold w-48 text-center">
              {format(nextMonth, "MMMM yyyy")}
            </h3>
          </div>
        )}

        <button
          type="button"
          onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
          className="p-2 rounded-full hover:bg-gray-100"
          aria-label="Next month"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar Content */}
      <div className="flex-1 overflow-hidden p-2 md:p-4 md:px-8">
        {/* Mobile: Different behavior for details page vs other pages */}
        <div className="md:hidden h-full">
          {isDetailsPage ? (
            // Details page mobile: Single month view with navigation only
            <div className="h-full">{renderMobileMonth(currentMonth, 0)}</div>
          ) : (
            // Other pages mobile: Vertical stacked months
            <div
              className="overflow-y-auto h-full -mx-2 px-2"
              style={{
                maxHeight: "calc(80vh - 130px)",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div className="flex flex-col space-y-6">
                {generateMonthsArray().map((month, index) =>
                  renderMobileMonth(month, index),
                )}

                {/* Load More Button - Non-details pages only */}
                <div className="flex justify-center py-2">
                  <button
                    type="button"
                    onClick={loadMoreMonths}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-ink-black transition-colors"
                  >
                    Load more months
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: Single or dual month view based on isMonthSingleView */}
        <div
          className={`hidden md:grid ${isMonthSingleView ? "md:grid-cols-1" : "md:grid-cols-2 md:gap-16"}`}
        >
          {isMonthSingleView
            ? renderDesktopMonth(currentMonth)
            : [currentMonth, nextMonth].map((month) =>
                renderDesktopMonth(month),
              )}
        </div>
      </div>

      {isDetailsPage ? (
        <div className="mt-6 p-4 md:mt-8 pt-4 md:pt-6  flex flex-col md:flex-row md:justify-end items-center space-y-3 md:space-y-0 flex-shrink-0">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => onDateChange({ checkIn: null, checkOut: null })}
              className="px-3 py-1 md:px-4 md:py-2 text-sm  rounded-lg underline transition-colors"
            >
              Clear dates
            </button>
            {/*<button*/}
            {/*  type="button"*/}
            {/*  onClick={onConfirm}*/}
            {/*  disabled={!selectedDates.checkIn || !selectedDates.checkOut}*/}
            {/*  className="block px-6 py-2 text-sm bg-coral-pink-50 text-brand-dark rounded-lg hover:bg-coral-pink-100 disabled:bg-gray-300 disabled:text-black disabled:cursor-not-allowed transition-colors"*/}
            {/*>*/}
            {/*  Update*/}
            {/*</button>*/}
          </div>
        </div>
      ) : (
        <div
          className={`flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0 flex-shrink-0 ${isPopover ? "mt-1 border-t border-[#EBEBEB] p-4" : "mt-6 p-4 md:mt-8 pt-4 md:pt-6"}`}
        >
          <div className="text-sm text-ink-secondary text-center md:text-left">
            {dynamicTitle}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => onDateChange({ checkIn: null, checkOut: null })}
              className={`px-3 py-1 md:px-4 md:py-2 text-sm underline rounded-lg transition-colors ${isPopover ? "font-semibold text-ink" : ""}`}
            >
              Clear dates
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={!selectedDates.checkIn || !selectedDates.checkOut}
              className={`hidden md:block px-6 text-sm text-white disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors ${isPopover ? "rounded-full bg-brand-dark py-2.5 font-semibold hover:opacity-90" : "rounded-lg bg-[#C2185B] py-2 hover:bg-[#A81648]"}`}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Show alertMsg if present */}
      {alertMsg && (
        <div className="w-full text-center py-2 text-sm text-danger bg-red-50 rounded mb-2">
          {alertMsg}
        </div>
      )}
    </div>
  );
};

export default DatePicker;
